import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { fontBarlowSemiCondensed, fontRoboto } from "shared/lib/styles";

import type { Props } from ".";
import { FollowButtonOutline, FollowButtonSize, FollowButtonVariant } from "./enums";
import BlueFollowIcon from "./icon-blue-follow.svg";
import BluePlusIcon from "./icon-blue-plus.svg";
import BlueSpinningLoadingIcon from "./icon-blue-spinning-loading.svg";
import BlueSquareFollowIcon from "./icon-blue-square-follow.svg";
import GrayCircleBlackTickFollowingIcon from "./icon-gray-circle-black-tick-following.svg";
import GrayCircleFollowIcon from "./icon-gray-circle-follow.svg";
import GrayCircleWhiteTickFollowingIcon from "./icon-gray-circle-white-tick-following.svg";

// TODO: Remove sdkTouchOptimizedStyles and use touchOptimizedStyles when sdk components are migrated to shared or apps
export const sdkTouchOptimizedStyles = css`
  user-select: none; /* Prevent text selection */
  touch-action: manipulation; /* Remove 200ms double tap input delay */
  -webkit-tap-highlight-color: transparent; /* Remove highlight */
`;

export const StyledBlueSpinningLoadingIcon = styled(BlueSpinningLoadingIcon)``;
export const StyledBlueSquareFollowIcon = styled(BlueSquareFollowIcon)``;
export const StyledBlueFollowIcon = styled(BlueFollowIcon)``;
export const StyledGrayCircleFollowIcon = styled(GrayCircleFollowIcon)``;
export const StyledGrayCircleBlackTickFollowingIcon = styled(GrayCircleBlackTickFollowingIcon)``;
export const StyledGrayCircleWhiteTickFollowingIcon = styled(GrayCircleWhiteTickFollowingIcon)``;

export const BluePlusFollowingIconContainer = styled.div``;
export const BluePlusFollowingText = styled.span`
  margin-inline-start: 2px;
`;
export const StyledBluePlusIcon = styled(BluePlusIcon)``;

type GetHomepageFollowButtonBorderColorParameters = Pick<
  StyledButtonProps,
  "isLoading" | "outline"
>;

const getHomepageFollowButtonBorderColor = ({
  isLoading,
  outline,
}: GetHomepageFollowButtonBorderColorParameters) => {
  if (outline === FollowButtonOutline.Plain) {
    return "transparent";
  }

  if (isLoading) {
    return "#4585ff";
  }

  return "#cccccc";
};

type StyledButtonProps = Omit<Props, "onClick">;

export const StyledButton = styled.button<StyledButtonProps>`
  display: inline-flex;
  justify-content: center;
  align-items: center;

  text-transform: uppercase;

  border-style: solid;
  border-width: ${props => (props.outline === FollowButtonOutline.Plain ? "0" : "1px")};
  border-radius: 2px;

  ${StyledBlueSquareFollowIcon},
  ${StyledBlueFollowIcon},
  ${StyledGrayCircleFollowIcon},
  ${StyledGrayCircleBlackTickFollowingIcon},
  ${StyledGrayCircleWhiteTickFollowingIcon} {
    inline-size: 18px;
    block-size: 18px;
  }

  ${props => {
    switch (props.size) {
      case FollowButtonSize.Compact:
        return css`
          inline-size: fit-content;
          padding: 0;

          font-size: 13px;
          font-family: ${fontRoboto};
          line-height: 15px;
          white-space: nowrap;

          overflow: hidden;

          ${StyledBlueSpinningLoadingIcon} {
            inline-size: 15px;
            block-size: 15px;
            margin-block: auto;
          }
        `;
      case FollowButtonSize.Full:
        return css`
          inline-size: 100%;
          padding-block: 6px;

          font-size: 12px;
          font-family: ${fontRoboto};
          line-height: 14px;

          ${StyledBlueSpinningLoadingIcon} {
            inline-size: 14px;
            block-size: 14px;
            margin-block: auto;
          }
        `;
      case FollowButtonSize.Large:
        return css`
          inline-size: 106px;
          block-size: 36px;
          padding-block: 10px;

          font-size: 14px;
          font-family: ${fontRoboto};
          line-height: 16px;

          ${StyledBlueSpinningLoadingIcon} {
            inline-size: 16px;
            block-size: 16px;
            margin-block: auto;
          }
        `;
      case FollowButtonSize.Small:
        return css`
          inline-size: 74px;
          padding-block: 7px;

          font-size: 11px;
          font-family: ${fontRoboto};
          line-height: 11px;

          ${StyledBlueSpinningLoadingIcon} {
            inline-size: 11px;
            block-size: 11px;
            margin-block: auto;
          }
        `;
      case FollowButtonSize.Thin:
        return css`
          inline-size: 70px;
          padding-block: 3px;

          font-size: 11px;
          font-family: ${fontRoboto};
          line-height: 13px;

          ${StyledBlueSpinningLoadingIcon} {
            inline-size: 13px;
            block-size: 13px;
            margin-block: auto;
          }
        `;
      default:
        return css`
          inline-size: 94px;
          padding-block: 8px;

          font-size: 12px;
          font-family: ${fontRoboto};
          line-height: 14px;
          text-align: center;

          ${StyledBlueSpinningLoadingIcon} {
            inline-size: 14px;
            block-size: 14px;
            margin-block: auto;
          }
        `;
    }
  }}

  ${props => {
    if (!props.isFollowing) return null;
    switch (props.variant) {
      case FollowButtonVariant.Dark:
        return css`
          color: #001246;

          border-color: ${props.outline === FollowButtonOutline.Plain ? "transparent" : "#ffffff"};

          background-color: ${props.outline === FollowButtonOutline.Plain
            ? "transparent"
            : "#ffffff"};
        `;
      case FollowButtonVariant.Homepage:
        return css`
          color: #000000;

          border-color: ${getHomepageFollowButtonBorderColor({
            isLoading: props.isLoading,
            outline: props.outline,
          })};

          background-color: ${props.outline === FollowButtonOutline.Plain || props.isLoading
            ? "transparent"
            : "#cccccc"};
        `;
      case FollowButtonVariant.Light:
        return css`
          color: #000000;

          border-color: ${props.outline === FollowButtonOutline.Plain ? "transparent" : "#e5e5e5"};

          background-color: ${props.outline === FollowButtonOutline.Plain
            ? "transparent"
            : "#e5e5e5"};
        `;
      case FollowButtonVariant.Style:
        return css`
          inline-size: 92px;

          color: #a1a1a1;
          font-weight: 600;
          font-family: ${fontBarlowSemiCondensed};
          letter-spacing: 0.36px;

          border-color: #a1a1a1;
          border-radius: 4px;

          background-color: #ffffff;
        `;
    }
  }}

  ${props => {
    if (props.isFollowing) return null;

    switch (props.variant) {
      case FollowButtonVariant.Dark:
        switch (props.outline) {
          case FollowButtonOutline.Filled:
            return css`
              color: #ffffff;

              border-color: #001246;

              background-color: #001246;
            `;
          case FollowButtonOutline.Outlined:
            return css`
              color: #ffffff;

              border-color: #ffffff;

              background-color: transparent;
            `;
          case FollowButtonOutline.Plain:
            return css`
              color: #ffffff;

              border-color: transparent;

              background-color: transparent;
            `;
        }
        break;
      case FollowButtonVariant.Homepage:
      case FollowButtonVariant.Light:
        switch (props.outline) {
          case FollowButtonOutline.Filled:
            return css`
              color: #ffffff;

              border-color: #001246;

              background-color: #001246;
            `;
          case FollowButtonOutline.Outlined:
            return css`
              color: #4585ff;

              border-color: #4585ff;

              background-color: transparent;
            `;
          case FollowButtonOutline.Plain:
            return css`
              color: #000000;

              border-color: transparent;

              background-color: transparent;
            `;
        }
        break;
      case FollowButtonVariant.Style:
        switch (props.outline) {
          case FollowButtonOutline.Outlined:
            return css`
              inline-size: 83px;
              block-size: 30px;
              padding: 0;

              color: #222222;
              font-weight: 600;
              font-family: ${fontBarlowSemiCondensed};
              letter-spacing: 0.36px;

              border-color: #222222;
              border-radius: 4px;

              background-color: #ffffff;
            `;
        }
    }
  }}

  &:not(:disabled) {
    cursor: pointer;
  }

  ${sdkTouchOptimizedStyles};
`;
